import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { trackGetStartedClick } from '../utils/analytics';
import { useCookieConsent } from '../contexts/CookieConsentContext';
import './Footer.css';

import ScheduleDemoModal from './ScheduleDemoModal';
import ContactSalesModal from './ContactSalesModal';

const Footer = () => {
  const [showScheduleDemoModal, setShowScheduleDemoModal] = useState(false);
  const [showContactSalesModal, setShowContactSalesModal] = useState(false);
  const { openSettings: openCookieSettings } = useCookieConsent();
  const navigate = useNavigate();

  // Redirect to testmindfrontend (app.qate.ai) for signup
  const saasUrl = process.env.REACT_APP_SAAS_URL || 'https://app.qate.ai';

  const handleFeatureClick = (e, sectionId) => {
    e.preventDefault();
    // If not on home page, navigate there first
    if (window.location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <footer className="footer">
      <div className="footer-container">
        <div className="footer-grid">
          {/* Logo/Brand Section */}
          <div className="footer-section footer-brand">
            <Link to="/" onClick={scrollToTop} className="footer-logo">
              <span className="footer-logo-text">QATE</span>
              <span className="footer-logo-ai">AI</span>
            </Link>
            <p className="footer-tagline">AI-Powered Testing Platform</p>
          </div>

          {/* Features Double Column */}
          <div className="footer-section footer-features">
            <h2 className="footer-heading">Features</h2>
            <div className="footer-links-double">
              <ul className="footer-links">
                <li>
                  <a href="/#conversational-exploration" onClick={(e) => handleFeatureClick(e, 'conversational-exploration')} className="footer-link-button">
                    Conversational Test Creation
                  </a>
                </li>
                <li>
                  <a href="/#windows-desktop" onClick={(e) => handleFeatureClick(e, 'windows-desktop')} className="footer-link-button">
                    Windows Desktop Testing
                  </a>
                </li>
                <li>
                  <a href="/#ai-suggestions" onClick={(e) => handleFeatureClick(e, 'ai-suggestions')} className="footer-link-button">
                    AI Test Suggestions
                  </a>
                </li>
                <li>
                  <a href="/#discovery" onClick={(e) => handleFeatureClick(e, 'discovery')} className="footer-link-button">
                    Discovery <span className="footer-badge-new">New</span>
                  </a>
                </li>
                <li>
                  <a href="/#ai-test-healing" onClick={(e) => handleFeatureClick(e, 'ai-test-healing')} className="footer-link-button">
                    AI Test Healing
                  </a>
                </li>
                <li>
                  <a href="/#ai-bug-detection" onClick={(e) => handleFeatureClick(e, 'ai-bug-detection')} className="footer-link-button">
                    AI Bug Detection
                  </a>
                </li>
                <li>
                  <a href="/#ai-bug-solving" onClick={(e) => handleFeatureClick(e, 'ai-bug-solving')} className="footer-link-button">
                    AI Bug Solving
                  </a>
                </li>
              </ul>
              <ul className="footer-links">
                <li>
                  <a href="/#github-copilot" onClick={(e) => handleFeatureClick(e, 'github-copilot')} className="footer-link-button">
                    AI Coding Agent
                  </a>
                </li>
                <li>
                  <a href="/#pr-processing" onClick={(e) => handleFeatureClick(e, 'pr-processing')} className="footer-link-button">
                    Smart PR / MR Processing
                  </a>
                </li>
                <li>
                  <a href="/#knowledge-base" onClick={(e) => handleFeatureClick(e, 'knowledge-base')} className="footer-link-button">
                    Knowledge Base
                  </a>
                </li>
                <li>
                  <a href="/#mfa-totp" onClick={(e) => handleFeatureClick(e, 'mfa-totp')} className="footer-link-button">
                    Multi-Factor Auth
                  </a>
                </li>
                <li>
                  <a href="/#enterprise-integrations" onClick={(e) => handleFeatureClick(e, 'enterprise-integrations')} className="footer-link-button">
                    Enterprise Integrations
                  </a>
                </li>
                <li>
                  <a href="/#reports-insights" onClick={(e) => handleFeatureClick(e, 'reports-insights')} className="footer-link-button">
                    Reports & Insights
                  </a>
                </li>
                <li>
                  <a href="/#visual-model" onClick={(e) => handleFeatureClick(e, 'visual-model')} className="footer-link-button">
                    Visual Application Model
                  </a>
                </li>
                <li>
                  <a href="/#no-vendor-lock-in" onClick={(e) => handleFeatureClick(e, 'no-vendor-lock-in')} className="footer-link-button">
                    No Vendor Lock-In
                  </a>
                </li>
              </ul>
            </div>
          </div>

          {/* Product Column */}
          <div className="footer-section">
            <h2 className="footer-heading">Product</h2>
            <ul className="footer-links">
              <li>
                <a href="/#how-it-works" onClick={(e) => handleFeatureClick(e, 'how-it-works')} className="footer-link-button">
                  How It Works
                </a>
              </li>
              <li>
                <a href="/#pricing" onClick={(e) => handleFeatureClick(e, 'pricing')} className="footer-link-button">
                  Pricing
                </a>
              </li>
              <li>
                <Link to="/documentation" onClick={scrollToTop}>
                  Documentation
                </Link>
              </li>
            </ul>
          </div>

          {/* Resources & Legal Column */}
          <div className="footer-section">
            <h2 className="footer-heading">Resources</h2>
            <ul className="footer-links">
              <li>
                <a href="/blog">Blog</a>
              </li>
              <li>
                <a href="/use-cases">Use Cases</a>
              </li>
              <li>
                <a href="/documentation">Documentation</a>
              </li>
            </ul>
            <h2 className="footer-heading footer-sub-heading">Legal</h2>
            <ul className="footer-links">
              <li>
                <Link to="/privacy" onClick={scrollToTop}>
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link to="/terms" onClick={scrollToTop}>
                  Terms of Service
                </Link>
              </li>
              <li>
                <button onClick={openCookieSettings} className="footer-link-button">
                  Cookie Settings
                </button>
              </li>
            </ul>
          </div>

          {/* Get Started Column */}
          <div className="footer-section">
            <h2 className="footer-heading">Get Started</h2>
            <ul className="footer-links">
              <li>
                <a href={`${saasUrl}/get-started`} onClick={(e) => { e.preventDefault(); trackGetStartedClick('footer-get-started'); window.gtag_report_conversion(`${saasUrl}/get-started`); }}>
                  Get Started
                </a>
              </li>
              <li>
                <a href={`${saasUrl}/get-started`} onClick={(e) => { e.preventDefault(); trackGetStartedClick('footer-sign-up'); window.gtag_report_conversion(`${saasUrl}/get-started`); }}>
                  Sign Up
                </a>
              </li>
              <li>
                <a href="https://app.qate.ai" target="_blank" rel="noopener noreferrer">
                  Launch App
                </a>
              </li>
              <li>
                <button onClick={() => setShowContactSalesModal(true)} className="footer-link-button">
                  Contact Sales
                </button>
              </li>
              <li>
                <button onClick={() => setShowScheduleDemoModal(true)} className="footer-link-button">
                  Schedule Demo
                </button>
              </li>
            </ul>
          </div>
        </div>

        {/* Trust Badges */}
        <div className="footer-trust-badges">
          <div className="trust-badge">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <circle cx="12" cy="12" r="10"/>
              <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
              <path d="M2 12h20"/>
            </svg>
            <span>GDPR</span>
          </div>
          <div className="trust-badge">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M12 2L2 7l10 5 10-5-10-5z"/>
              <path d="M2 17l10 5 10-5"/>
              <path d="M2 12l10 5 10-5"/>
            </svg>
            <span>EU AI Act</span>
          </div>
          <div className="trust-badge">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
            </svg>
            <span>OWASP</span>
          </div>
          <div className="trust-badge">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <rect x="3" y="11" width="18" height="11" rx="2"/>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
            </svg>
            <span>Stripe Secure</span>
          </div>
          <div className="trust-badge">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
              <path d="M12 22V12"/>
              <path d="M3 8l9 4 9-4"/>
            </svg>
            <span>Made in EU</span>
          </div>
          <div className="trust-badge-separator"></div>
          <div className="trust-badge tech-badge">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
            </svg>
            <span>GitHub</span>
          </div>
          <div className="trust-badge tech-badge">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M22.65 14.39L12 22.13.35 14.39c-.18-.14-.25-.37-.18-.58L2.27 8.6l2.1 6.45h15.25l2.1-6.45 2.11 5.21c.07.21 0 .44-.18.58z"/>
              <path d="M12 22.13L17.62 15.05H6.38L12 22.13z"/>
              <path d="M6.38 15.05L2.27 8.6 12 22.13 6.38 15.05z"/>
              <path d="M2.27 8.6l-.92 2.83c-.08.26.01.54.23.71L12 22.13 2.27 8.6z"/>
              <path d="M2.27 8.6h4.11L4.38 2.55c-.1-.3-.52-.3-.62 0L2.27 8.6z"/>
              <path d="M17.62 15.05L21.73 8.6 12 22.13l5.62-7.08z"/>
              <path d="M21.73 8.6l.92 2.83c.08.26-.01.54-.23.71L12 22.13 21.73 8.6z"/>
              <path d="M21.73 8.6h-4.11l1.99-6.05c.1-.3.52-.3.62 0L21.73 8.6z"/>
            </svg>
            <span>GitLab</span>
          </div>
          <div className="trust-badge tech-badge">
            <svg viewBox="0 0 128 128" fill="currentColor">
              <path d="m72.086 86.132-.594-.144c-13.125-3.844-15.15-14.311-15.15-14.311l18.182 5.082L84.15 39.77l-.116-.031c-11.807-3.162-19.64-8.692-22.744-11.292-4.4-3.685-6.335-6.246-8.24-2.372-1.682 3.417-3.836 8.977-5.92 16.762-4.516 16.857-7.892 52.429 20.027 59.914l.572.129zm-18.807-30.85s4.4-6.843 11.862-4.722c7.467 2.121 8.045 10.376 8.045 10.376zm-8.517 23.451L31.787 82.41s1.41-8.029 10.968-11.212l-7.347-27.573-.635.193c-9.111 2.457-16.476 1.805-19.55 1.273-4.357-.751-6.636-1.708-6.422 1.606.186 2.923.882 7.454 2.477 13.44 3.45 12.961 14.854 37.937 36.405 32.132l.635-.199-3.555-13.337ZM19.548 60.315l15.316-4.035s-.446 5.892-6.188 7.405c-5.743 1.512-9.128-3.371-9.128-3.371zm89.824-18.979c-3.981.698-13.532 1.567-25.336-1.596-11.807-3.162-19.64-8.692-22.744-11.292-4.4-3.685-6.335-6.246-8.24-2.372-1.684 3.417-3.837 8.977-5.921 16.762-4.516 16.857-7.892 52.429 20.027 59.914 27.912 7.479 42.772-25.017 47.289-41.875 2.084-7.783 2.998-13.676 3.25-17.476.287-4.305-2.67-3.055-8.324-2.064zM53.28 55.282s4.4-6.843 11.862-4.722c7.467 2.121 8.045 10.376 8.045 10.376zm18.215 30.706c-13.125-3.845-15.15-14.311-15.15-14.311l35.259 9.858c0-.002-7.117 8.25-20.109 4.453zm12.466-21.51s4.394-6.838 11.854-4.711c7.46 2.124 8.048 10.379 8.048 10.379z"/>
            </svg>
            <span>Playwright</span>
          </div>
          <div className="trust-badge tech-badge">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M0 3.449L9.75 2.1v9.451H0m10.949-9.602L24 0v11.4H10.949M0 12.6h9.75v9.451L0 20.699M10.949 12.6H24V24l-12.9-1.801"/>
            </svg>
            <span>Windows</span>
          </div>
        </div>

        {/* Summarize with AI */}
        <div className="footer-ai-summarize">
          <span className="ai-summarize-label">Summarize with AI</span>
          <div className="ai-summarize-links">
            <a href="https://chatgpt.com/?q=Summarize%20the%20QATE%20AI%20website%20at%20https%3A%2F%2Fqate.ai%20-%20a%20GenAI%20native%20testing%20platform%20for%20web%2C%20Windows%20desktop%2C%20and%20API%20testing.%20What%20are%20its%20key%20features%20and%20value%20proposition%3F" target="_blank" rel="noopener noreferrer" className="ai-link" title="ChatGPT">
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M22.282 9.821a5.985 5.985 0 0 0-.516-4.91 6.046 6.046 0 0 0-6.51-2.9A6.065 6.065 0 0 0 4.981 4.18a5.985 5.985 0 0 0-3.998 2.9 6.046 6.046 0 0 0 .743 7.097 5.98 5.98 0 0 0 .51 4.911 6.051 6.051 0 0 0 6.515 2.9A5.985 5.985 0 0 0 13.26 24a6.056 6.056 0 0 0 5.772-4.206 5.99 5.99 0 0 0 3.997-2.9 6.056 6.056 0 0 0-.747-7.073zM13.26 22.43a4.476 4.476 0 0 1-2.876-1.04l.141-.081 4.779-2.758a.795.795 0 0 0 .392-.681v-6.737l2.02 1.168a.071.071 0 0 1 .038.052v5.583a4.504 4.504 0 0 1-4.494 4.494zM3.6 18.304a4.47 4.47 0 0 1-.535-3.014l.142.085 4.783 2.759a.771.771 0 0 0 .78 0l5.843-3.369v2.332a.08.08 0 0 1-.033.062L9.74 19.95a4.5 4.5 0 0 1-6.14-1.646zM2.34 7.896a4.485 4.485 0 0 1 2.366-1.973V11.6a.766.766 0 0 0 .388.676l5.815 3.355-2.02 1.168a.076.076 0 0 1-.071 0l-4.83-2.786A4.504 4.504 0 0 1 2.34 7.896zm16.597 3.855l-5.833-3.387L15.119 7.2a.076.076 0 0 1 .071 0l4.83 2.791a4.494 4.494 0 0 1-.676 8.105v-5.678a.79.79 0 0 0-.407-.667zm2.01-3.023l-.141-.085-4.774-2.782a.776.776 0 0 0-.785 0L9.409 9.23V6.897a.066.066 0 0 1 .028-.061l4.83-2.787a4.5 4.5 0 0 1 6.68 4.66zm-12.64 4.135l-2.02-1.164a.08.08 0 0 1-.038-.057V6.075a4.5 4.5 0 0 1 7.375-3.453l-.142.08-4.778 2.758a.795.795 0 0 0-.393.681zm1.097-2.365l2.602-1.5 2.607 1.5v2.999l-2.597 1.5-2.607-1.5z"/>
              </svg>
            </a>
            <a href="https://www.perplexity.ai/search?q=Summarize%20the%20QATE%20AI%20website%20at%20https%3A%2F%2Fqate.ai%20-%20a%20GenAI%20native%20testing%20platform%20for%20web%2C%20Windows%20desktop%2C%20and%20API%20testing.%20What%20are%20its%20key%20features%20and%20value%20proposition%3F" target="_blank" rel="noopener noreferrer" className="ai-link" title="Perplexity">
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 1L5.5 5.5V11h-3v7.5L7 22h5v-6.5h-1V22h6l4.5-3.5V11h-3V5.5L12 1zm0 2l5 3.5V11H7V6.5L12 3zM4.5 13h3v5.5L5.5 17v-4h-1zm12 0h3v4l-2 1.5V13h1zm-8 0h7v3.5l-3.5 2.5-3.5-2.5V13z"/>
              </svg>
            </a>
            <a href="https://claude.ai/new?q=Summarize%20the%20QATE%20AI%20website%20at%20https%3A%2F%2Fqate.ai%20-%20a%20GenAI%20native%20testing%20platform%20for%20web%2C%20Windows%20desktop%2C%20and%20API%20testing.%20What%20are%20its%20key%20features%20and%20value%20proposition%3F" target="_blank" rel="noopener noreferrer" className="ai-link" title="Claude">
              <svg viewBox="0 0 16 16" fill="currentColor">
                <path fillRule="evenodd" d="M9.218 2h2.402L16 12.987h-2.402zM4.379 2h2.512l4.38 10.987H8.82l-.895-2.308h-4.58l-.896 2.307H0L4.38 2.001zm2.755 6.64L5.635 4.777 4.137 8.64z"/>
              </svg>
            </a>
            <a href="https://x.com/i/grok?text=Summarize%20the%20QATE%20AI%20website%20at%20https%3A%2F%2Fqate.ai%20-%20a%20GenAI%20native%20testing%20platform%20for%20web%2C%20Windows%20desktop%2C%20and%20API%20testing.%20What%20are%20its%20key%20features%20and%20value%20proposition%3F" target="_blank" rel="noopener noreferrer" className="ai-link" title="Grok">
              <svg viewBox="0 0 512 510" fill="currentColor">
                <path d="M213.235 306.019l178.976-180.002v.169l51.695-51.763c-.924 1.32-1.86 2.605-2.785 3.89-39.281 54.164-58.46 80.649-43.07 146.922l-.09-.101c10.61 45.11-.744 95.137-37.398 131.836-46.216 46.306-120.167 56.611-181.063 14.928l42.462-19.675c38.863 15.278 81.392 8.57 111.947-22.03 30.566-30.6 37.432-75.159 22.065-112.252-2.92-7.025-11.67-8.795-17.792-4.263l-124.947 92.341zm-25.786 22.437l-.033.034L68.094 435.217c7.565-10.429 16.957-20.294 26.327-30.149 26.428-27.803 52.653-55.359 36.654-94.302-21.422-52.112-8.952-113.177 30.724-152.898 41.243-41.254 101.98-51.661 152.706-30.758 11.23 4.172 21.016 10.114 28.638 15.639l-42.359 19.584c-39.44-16.563-84.629-5.299-112.207 22.313-37.298 37.308-44.84 102.003-1.128 143.81z"/>
              </svg>
            </a>
            <a href="https://gemini.google.com/app?q=Summarize%20the%20QATE%20AI%20website%20at%20https%3A%2F%2Fqate.ai%20-%20a%20GenAI%20native%20testing%20platform%20for%20web%2C%20Windows%20desktop%2C%20and%20API%20testing.%20What%20are%20its%20key%20features%20and%20value%20proposition%3F" target="_blank" rel="noopener noreferrer" className="ai-link" title="Google Gemini">
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 24C12 24 12 12 24 12C12 12 12 0 12 0C12 0 12 12 0 12C12 12 12 24 12 24Z"/>
              </svg>
            </a>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="footer-bottom">
          <p>&copy; 2026 Qate AI. All rights reserved.</p>
        </div>
      </div>

      {/* Modals */}
      {showScheduleDemoModal && (
        <ScheduleDemoModal onClose={() => setShowScheduleDemoModal(false)} />
      )}
      {showContactSalesModal && (
        <ContactSalesModal onClose={() => setShowContactSalesModal(false)} />
      )}
    </footer>
  );
};

export default Footer;
